import { SyntheticEvent, useRef } from 'react';

/**
 * Tracks the caret of a text input so external actions (e.g. a variables
 * picker) can splice text at the cursor instead of appending at the end.
 *
 * The selection is captured via onSelect rather than read live: a
 * never-focused input reports selectionStart 0, which would insert at the
 * start instead of appending.
 */
export const useInsertTextAtCursor = () => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectionRef = useRef<{ start: number; end: number } | null>(null);

  const handleSelect = (event: SyntheticEvent<HTMLInputElement>) => {
    const { selectionStart, selectionEnd } = event.currentTarget;
    selectionRef.current = {
      start: selectionStart ?? 0,
      end: selectionEnd ?? 0,
    };
  };

  const insertTextAtCursor = (value: string, text: string) => {
    const { start, end } = selectionRef.current ?? {
      start: value.length,
      end: value.length,
    };
    const cursor = start + text.length;
    selectionRef.current = { start: cursor, end: cursor };
    requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(cursor, cursor);
    });
    return value.slice(0, start) + text + value.slice(end);
  };

  const resetSelection = () => {
    selectionRef.current = null;
  };

  return { inputRef, handleSelect, insertTextAtCursor, resetSelection };
};
